import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import SVG from "react-inlinesvg";
import { openAlert, toAbsoluteUrl } from "../../_metronic/_helpers";
import { PlanetHdPoolPanel } from "../components/PlanetHdPoolPanel";
import { ResourceSummaryTable } from "../components/ResourceSummaryTable";
import { formatDecimal, formatInteger } from "../utils/formatNumber";
import {
  getSharedAvailableHa,
  isEffectivelyUnlimitedAvailability,
} from "../utils/resources";
import * as StatsService from "./StatsService";

const AMBIENT_RESOURCE_IDS = ["ambients", "reseller_ambients_ha"];
const REPORT_RESOURCE_IDS = ["reports_ndvi_ha", "reseller_reports_ndvi_ha"];
const initialSource = { loading: true, error: false, data: null };

const getPoolList = response => {
  const payload = response?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const currentPool = pools => pools.find(pool => pool.status === "active") || null;

export function DashboardPage() {
  const history = useHistory();
  const [tenant, setTenant] = useState(initialSource);
  const [payment, setPayment] = useState(initialSource);
  const [planet, setPlanet] = useState(initialSource);
  const [updatedAt, setUpdatedAt] = useState(null);

  const loadTenant = useCallback(() => {
    setTenant(value => ({ ...value, loading: true, error: false }));
    return StatsService.getTenantStats()
      .then(response => setTenant({ loading: false, error: false, data: response.data }))
      .catch(() => setTenant({ loading: false, error: true, data: null }));
  }, []);

  const loadPayment = useCallback(() => {
    setPayment(value => ({ ...value, loading: true, error: false }));
    return StatsService.getTenantPaymentStats()
      .then(response => setPayment({ loading: false, error: false, data: response.data }))
      .catch(() => setPayment({ loading: false, error: true, data: null }));
  }, []);

  const loadPlanet = useCallback(() => {
    setPlanet(value => ({ ...value, loading: true, error: false }));
    return StatsService.getPlanetHdPools()
      .then(response => setPlanet({ loading: false, error: false, data: getPoolList(response) }))
      .catch(() => setPlanet({ loading: false, error: true, data: null }));
  }, []);

  const refresh = useCallback(() => (
    Promise.all([
      loadTenant().catch(() => null),
      loadPayment().catch(() => null),
      loadPlanet().catch(() => null),
    ]).then(() => setUpdatedAt(new Date()))
  ), [loadTenant, loadPayment, loadPlanet]);

  const createPool = data => StatsService.createPlanetHdPool(data).then(response => {
    openAlert("success", "Completado", "Período de Planet HD registrado");
    return loadPlanet().then(() => ({ ok: true, data: response.data }));
  }).catch(error => {
    const payload = error.response?.data || {};
    openAlert("error", "Error", payload.message || payload.error || "No se pudo registrar el período");
    return { ok: false, message: payload.message || payload.error };
  });

  const reconcilePool = poolId => StatsService.reconcilePlanetHdPool(poolId).then(response => {
    openAlert("success", "Completado", "Reconciliación ejecutada");
    return loadPlanet().then(() => ({ ok: true, data: response.data }));
  }).catch(error => {
    const payload = error.response?.data || {};
    openAlert("error", "Error", payload.message || payload.error || "No se pudo reconciliar el período");
    return { ok: false, message: payload.message || payload.error };
  });

  useEffect(() => {
    refresh();
  }, [refresh]);

  const pending = payment.data?.pending_resource_to_assign || [];
  const availableAmbients = payment.error
    ? null
    : getSharedAvailableHa(pending, AMBIENT_RESOURCE_IDS);
  const availableReports = payment.error
    ? null
    : getSharedAvailableHa(pending, REPORT_RESOURCE_IDS);
  const pool = currentPool(planet.data || []);
  const availablePlanet = planet.error || !pool ? null : pool.remaining_assignable_ha;
  const planetUsed = pool
    ? Number(pool.covered_ha || 0) + Number(pool.reserved_ha || 0)
    : null;
  const planetRemaining = pool
    ? Math.max(Number(pool.committed_ha || 0) - planetUsed, 0)
    : null;
  const notices = [
    payment.error && {
      key: "standard",
      message: "Los recursos Standard no están disponibles. No se muestran ceros porque no representan un saldo confirmado.",
      onRetry: loadPayment,
    },
    planet.error && {
      key: "planet",
      message: "Los recursos de Planet HD no están disponibles.",
      onRetry: loadPlanet,
    },
    !planet.loading && !planet.error && !pool && {
      key: "planet-pool",
      message: "No hay un período vigente de Planet HD.",
      onRetry: loadPlanet,
    },
  ].filter(Boolean);

  return (
    <div className="admin-dashboard">
      <div className="admin-dashboard__toolbar">
        <span>
          Última actualización: {updatedAt ? updatedAt.toLocaleString("es-AR") : "—"}
        </span>
        <button type="button" className="btn btn-light-primary" onClick={refresh}>
          Actualizar
        </button>
      </div>
      <div className="admin-dashboard__metrics">
        <Tile
          count={tenant.error ? null : tenant.data?.count_users}
          isLoading={tenant.loading}
          text="Usuarios"
          icon={toAbsoluteUrl("/media/svg/icons/Communication/Group.svg")}
          onClick={() => history.push("/users")}
          onRetry={tenant.error ? loadTenant : null}
        />
        <AvailabilityTile value={availableAmbients} loading={payment.loading} text="Hectáreas disponibles para ambientar" onRetry={payment.error ? loadPayment : null} />
        <AvailabilityTile value={availableReports} loading={payment.loading} text="Hectáreas disponibles para reportes" onRetry={payment.error ? loadPayment : null} />
        <AvailabilityTile value={availablePlanet} loading={planet.loading} text="Hectáreas disponibles de Planet HD" onRetry={planet.error ? loadPlanet : null} />
      </div>
      <section className="admin-dashboard-resources">
        <h3 className="admin-dashboard-resources__title">Recursos</h3>
        <ResourceSummaryTable
          assignedResources={payment.data?.assigned_resources || []}
          usageResources={payment.data?.usage_resources || []}
          isLoading={payment.loading && planet.loading}
          includeUsageOnly={false}
          additionalRows={[{
            resourceId: "planet_hd_ha",
            label: "Hectáreas de Planet HD",
            assigned: pool?.committed_ha ?? null,
            used: planetUsed,
            remaining: planetRemaining,
          }]}
          notices={notices}
        />
      </section>
      <PlanetHdPoolPanel
        pools={planet.data || []}
        loading={planet.loading}
        error={planet.error}
        onReload={loadPlanet}
        onCreate={createPool}
        onReconcile={reconcilePool}
      />
    </div>
  );
}

const LoadingValue = ({ loading, value, formatter }) => (
  loading ? (
    <span className="spinner-border spinner-border-sm text-primary" role="status">
      <span className="sr-only">Cargando...</span>
    </span>
  ) : value === null || value === undefined ? "—" : formatter(value)
);

const AvailabilityTile = ({ value, loading, text, onRetry }) => (
  <div className="card card-custom admin-metric-card admin-metric-card--availability">
    <div className="card-body">
      <div className="admin-metric-card__content">
        <div className="text-dark font-weight-bolder font-size-h2">
          <LoadingValue
            loading={loading}
            value={value}
            formatter={amount => isEffectivelyUnlimitedAvailability(amount)
              ? "Sin límite"
              : formatDecimal(amount)}
          />
        </div>
        <div className="text-muted font-weight-bold font-size-lg mt-1">{text}</div>
        {onRetry && <button type="button" className="btn btn-link p-0 mt-2" onClick={onRetry}>Reintentar</button>}
      </div>
    </div>
  </div>
);

const Tile = ({ count, text, icon, isLoading, onClick, onRetry }) => (
  <button
    type="button"
    className="card card-custom admin-metric-card admin-metric-card--clickable"
    onClick={onRetry || onClick}
  >
    <div className="card-body">
      <span className="svg-icon svg-icon-3x"><SVG src={icon} /></span>
      <div className="admin-metric-card__content">
        <div className="text-dark font-weight-bolder font-size-h2 mt-3">
          <LoadingValue loading={isLoading} value={count} formatter={formatInteger} />
        </div>
        <div className="text-muted font-weight-bold font-size-lg mt-1">{text}</div>
        {onRetry && <span className="text-primary mt-2">Reintentar</span>}
      </div>
    </div>
  </button>
);
